import { AddCircleOutlineOutlined, Cancel, CloudDownload } from '@mui/icons-material'
import { Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, SxProps, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMutation } from 'react-query'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { backendClient } from '../../../../interactors/clients/client'
import { AnimalDto } from '../../../../interactors/gen/backendClient'
import { useAnimalStore } from '../../../../store/AnimalStore'
import { useGlobalSnackbarStore } from '../../../../store/GlobalSnackBarStore'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'
import { getAnimalProfilePhotoSrc } from '../../../../utils/S3-links'
import { colors } from '../../../theme'

interface Props {
  animal: AnimalDto
  standalone?: boolean
  sx?: SxProps
}

export const AnimalOtherImagesCard: React.FC<Props> = ({ animal, standalone = false, sx }) => {
  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    accept: { 'image/*': [] },
    multiple: false,
  })
  const globalSnackbar = useGlobalSnackbarStore()
  const animalStore = useAnimalStore()
  const canEditAnimal = useCanEditAnimal()

  const [pictures, setPictures] = React.useState<string[]>(animal.images?.otherImagesKeys || [])

  const addImageMutation = useMutation(
    async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return await backendClient.post(`animals/${animal.id}/images/other`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    {
      onSuccess: (response) => {
        globalSnackbar.triggerSuccessMessage(`Votre photo a bien été ajoutée !`)
        const parts = response.data.split('/')
        setPictures((pictures) => [...pictures, parts[parts.length - 1]])
        animalStore.changeSelectedAnimal(animal.id)
      },
    }
  )

  const deleteImageMutation = useMutation(
    async (imageKey: string) => {
      return await backendClient.delete(`animals/${animal.id}/images/other/${imageKey}`)
    },
    {
      onSuccess: (_data, variables) => {
        globalSnackbar.triggerSuccessMessage(`Votre photo a bien été supprimée !`)
        setPictures((pictures) => pictures.filter((picture) => picture !== variables))
      },
    }
  )

  useEffect(() => {
    if (acceptedFiles[0] && (acceptedFiles[0].type === 'image/jpeg' || acceptedFiles[0].type === 'image/png')) {
      addImageMutation.mutate(acceptedFiles[0])
    }
  }, [acceptedFiles])

  const carouselSettings = {
    infinite: pictures && pictures.length > 3,
    dots: false,
    autoplay: false,
    slidesToShow: Math.min(4, pictures.length),
    slidesToScroll: 1,
    initialSlide: 0,
    speed: 180,
    arrows: false,
    swipeToSlide: true,
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: Math.min(3, pictures.length) } },
      { breakpoint: 900, settings: { slidesToShow: Math.min(3, pictures.length) } },
      { breakpoint: 600, settings: { slidesToShow: Math.min(2.2, pictures.length) } },
    ],
  }

  const content = (
    <CardContent sx={{ pt: standalone ? 1 : 0, py: standalone ? '12px !important' : '10px !important' }}>
      {addImageMutation.isLoading ? (
        <CircularProgress sx={{ pt: 2 }} />
      ) : pictures.length > 0 ? (
        <Box sx={{ position: 'relative', width: '100%' }}>
          {/* Horizontal slider (thumbnails) */}
          <Box
            sx={{
              width: '100%',
              border: '1px solid rgba(228, 232, 242, 0.85)',
              backgroundColor: '#FFFFFF',
              borderRadius: 3.5,
              px: 1,
              py: 0.9,
            }}
          >
            <Slider {...carouselSettings}>
              {pictures.map((imageKey) => (
                <div key={imageKey}>
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '0 6px',
                    }}
                  >
                    <img
                      src={getAnimalProfilePhotoSrc(imageKey)}
                      alt={imageKey}
                      style={{
                        // Keep original proportions: fixed height + auto width.
                        height: '104px',
                        width: 'auto',
                        maxWidth: '160px',
                        objectFit: 'contain',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#F7F9FD',
                      }}
                    />
                    {canEditAnimal && (
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 10,
                          color: 'white',
                          backgroundColor: 'rgba(23, 31, 52, 0.38)',
                          zIndex: 999,
                          '&:hover': { backgroundColor: 'rgba(23, 31, 52, 0.55)' },
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteImageMutation.mutate(imageKey)
                        }}
                      >
                        <Cancel sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: 6,
                        right: 10,
                        color: 'white',
                        backgroundColor: 'rgba(23, 31, 52, 0.38)',
                        zIndex: 999,
                        '&:hover': { backgroundColor: 'rgba(23, 31, 52, 0.55)' },
                      }}
                      component="a"
                      href={getAnimalProfilePhotoSrc(imageKey)}
                      download
                    >
                      <CloudDownload sx={{ fontSize: 18 }} />
                    </IconButton>
                  </div>
                </div>
              ))}
            </Slider>
          </Box>

          {/* Full-width button */}
          {canEditAnimal && (
            <Box sx={{ width: '100%', mt: 1.25, textAlign: 'center' }}>
              <Button
                {...getRootProps()}
                sx={{
                  textTransform: 'none',
                  borderRadius: '999px',
                  minHeight: 34,
                  px: 2,
                  fontWeight: 700,
                  fontSize: 12.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.72)',
                  borderColor: 'rgba(122, 129, 151, 0.24)',
                  color: colors.cello,
                  border: '1px solid rgba(122, 129, 151, 0.24)',
                  '&:hover': { backgroundColor: 'white', borderColor: 'rgba(122, 129, 151, 0.35)' },
                }}
                fullWidth
              >
                Ajouter une photo
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        // Add Photo (Full Width)
        canEditAnimal && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            padding={2}
            {...getRootProps({
              sx: {
                minHeight: { xs: 150, sm: standalone ? 190 : 180, md: standalone ? 230 : 180 },
                border: '1.5px dashed',
                borderRadius: 4,
                borderStyle: 'dashed',
                borderColor: isDragActive ? '#77A96B' : 'rgba(105, 141, 172, 0.38)',
                backgroundColor: isDragActive ? 'rgba(168, 213, 186, 0.2)' : 'rgba(247, 249, 253, 0.92)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center',
                '&:hover': {
                  borderColor: 'rgba(105, 141, 172, 0.55)',
                  backgroundColor: '#FFFFFF',
                },
              },
            })}
          >
            <input {...getInputProps()} />
            <AddCircleOutlineOutlined sx={{ color: colors.blue, fontSize: 34, mb: 0.75 }} />
            <Typography variant="h4" component="p" fontSize={15} fontWeight={700} textAlign="center">
              Ajouter une photo
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: 11.5, fontWeight: 400, color: '#7A8197', lineHeight: 1.35 }}>
              Glissez une image ici ou cliquez pour importer
            </Typography>
          </Box>
        )
      )}
    </CardContent>
  )

  if (!standalone) {
    return content
  }

  return (
    <Card sx={sx}>
      <Box sx={{ px: 2, pt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#7A8197' }}>Galerie</Typography>
        <Chip
          label={pictures.length}
          size="small"
          sx={{
            height: 20,
            backgroundColor: '#EEF2F8',
            color: colors.cello,
            '& .MuiChip-label': {
              px: 0.8,
              fontSize: 11,
              fontWeight: 600,
            },
          }}
        />
      </Box>
      {content}
    </Card>
  )
}
